import { Product } from '../../models/interfaces';

// Modificadores de un producto como vienen en el menú QR (/menu/getMenuQR). Una opción sin productId es gratis y va
// en la nota de la línea; con productId es un extra que se agrega como línea de ese producto.

export interface QrModifierOption {
    id: number;
    name: string;
    productId: number | null;
    price: number;
}

export interface QrModifierGroup {
    id: number;
    name: string;
    minSelect: number;
    maxSelect: number;   // 0 = sin límite
    required: boolean;
    options: QrModifierOption[];
}

export type QrProduct = Product & { modifierGroups?: QrModifierGroup[]; message?: string };

// El API guarda hasta 200 caracteres de nota por producto en los pedidos del menú QR
export const MAX_NOTE_LENGTH = 200;

export const hasModifiers = (product: QrProduct) => (product.modifierGroups?.length ?? 0) > 0;

export const groupRule = (g: QrModifierGroup) => {
    const { minSelect: min, maxSelect: max } = g;
    if (min > 0 && max > 0 && min === max) return `Elija ${min}`;
    if (min > 0) return max > 0 ? `Elija de ${min} a ${max}` : `Elija al menos ${min}`;
    return max > 0 ? `Opcional, hasta ${max}` : 'Opcional';
};

// Marca o desmarca una opción respetando el máximo del grupo (con máximo 1 reemplaza la elegida)
export const toggleOption = (group: QrModifierGroup, selected: number[], optionId: number): number[] => {
    if (selected.includes(optionId)) return selected.filter(id => id !== optionId);
    if (group.maxSelect === 1) return [optionId];
    if (group.maxSelect > 0 && selected.length >= group.maxSelect) return selected;
    return [...selected, optionId];
};

export const isComplete = (group: QrModifierGroup, selected: number[]) => selected.length >= group.minSelect;

// Opciones elegidas en el orden configurado
export const chosenOptions = (groups: QrModifierGroup[], selection: Record<number, number[]>): QrModifierOption[] =>
    groups.flatMap(g => g.options.filter(o => (selection[g.id] ?? []).includes(o.id)));

// "Término medio, Sin cebolla, + Extra queso"
export const choiceNote = (options: QrModifierOption[]) =>
    options.map(o => o.productId ? `+ ${o.name}` : o.name).join(', ');

// Agrega "1x <opciones>" a la nota de la línea
export const appendNote = (message: string | undefined, options: QrModifierOption[]) => {
    if (options.length === 0) return message ?? '';
    const entry = `1x ${choiceNote(options)}`;
    const current = (message ?? '').trim();
    return (current ? `${current} | ${entry}` : entry).slice(0, MAX_NOTE_LENGTH);
};
